'use client';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useCreateDemandExampleMutation,
  useDeleteDemandExampleMutation,
  useListDemandExamplesQuery,
  useUpdateDemandExampleMutation,
} from '../../services/api/demandExampleService';
import { MatterType } from '../../services/types/client-intake-types';
import {
  DemandExample,
  DemandExampleFilters,
} from '../../services/types/demand-example-types';
import { CreateDemandExampleModal } from './CreateDemandExampleModal';
import { DemandExampleDeleteModal } from './DemandExampleDeleteModal';
import { DemandExampleSelector } from './DemandExampleSelector';
import { DemandExamplesFilters } from './DemandExamplesFilters';
import { DemandExamplesHeader } from './DemandExamplesHeader';
import { DemandExamplesTable } from './DemandExamplesTable';
import { EditDemandExampleModal } from './EditDemandExampleModal';
import { FilePreviewModal } from './FilePreviewModal';

interface FormData {
  title: string;
  description: string;
  matter_type: MatterType | '';
  jurisdiction: string[];
  practice_areas: string[];
  tags: string[];
  firm_id: string | null;
  transform_id: string;
}

export const DemandExamples = () => {
  // State for filters and search
  const [filters, setFilters] = useState<Partial<DemandExampleFilters>>({
    ordering: '-created_at',
    limit: 25,
    offset: 0,
  });

  // State for modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [selectedExample, setSelectedExample] = useState<DemandExample | null>(
    null,
  );
  const [previewExample, setPreviewExample] = useState<DemandExample | null>(
    null,
  );
  const navigate = useNavigate();

  // Create form state (simplified)
  const [createTitle, setCreateTitle] = useState('');
  const [createFile, setCreateFile] = useState<File | null>(null);
  const [createFirmId, setCreateFirmId] = useState<string | null>(null);

  // Edit form state (full form)
  const [editFormData, setEditFormData] = useState<FormData>({
    title: '',
    description: '',
    matter_type: '',
    jurisdiction: [],
    practice_areas: [],
    tags: [],
    firm_id: '',
    transform_id: '',
  });

  // RTK Query hooks
  const {
    data: demandExamples,
    isLoading,
    error,
    refetch,
  } = useListDemandExamplesQuery(filters, {
    pollingInterval: 5000,
    refetchOnMountOrArgChange: true,
  });
  const [createExample, { isLoading: isCreating }] =
    useCreateDemandExampleMutation();
  const [updateExample, { isLoading: isUpdating }] =
    useUpdateDemandExampleMutation();
  const [deleteExample, { isLoading: isDeleting }] =
    useDeleteDemandExampleMutation();

  // Multi-select state for table page
  const [isMultiSelect, setIsMultiSelect] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Handlers
  const handleSearch = (search: string) => {
    setFilters((prev) => ({ ...prev, search, offset: 0 }));
  };

  const handleFilterChange = <K extends keyof DemandExampleFilters>(
    key: K,
    value: DemandExampleFilters[K],
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value, offset: 0 }));
  };

  const handlePageChange = (page: number) => {
    const offset = (page - 1) * (filters.limit || 25);
    setFilters((prev) => ({ ...prev, offset }));
  };

  const resetCreateForm = () => {
    setCreateTitle('');
    setCreateFile(null);
  };

  const resetEditForm = () => {
    setEditFormData({
      title: '',
      description: '',
      matter_type: '',
      jurisdiction: [],
      practice_areas: [],
      tags: [],
      firm_id: '',
      transform_id: '',
    });
  };

  const openCreateModal = () => {
    resetCreateForm();
    setShowCreateModal(true);
  };

  const openEditModal = (example: DemandExample) => {
    // Set all state together to prevent flicker
    setSelectedExample(example);
    setEditFormData({
      title: example.title || '',
      description: example.description || '',
      matter_type: example.matter_type || '',
      jurisdiction: example.jurisdiction || [],
      practice_areas: example.practice_areas || [],
      tags: example.tags || [],
      firm_id: example.firm_details?.id || '',
      transform_id: example.transform_details?.id || '',
    });

    // Use setTimeout to ensure state updates are completed before opening modal
    setTimeout(() => {
      setShowEditModal(true);
    }, 0);
  };

  const openDeleteModal = (example: DemandExample) => {
    setSelectedExample(example);
    setShowDeleteModal(true);
  };

  const openBulkDeleteConfirm = () => {
    if (selectedIds.length === 0) return;
    // Reuse single delete modal when 1 selected; otherwise custom confirm
    if (selectedIds.length === 1 && demandExamples) {
      const item = demandExamples.results.find((e) => e.id === selectedIds[0]);
      if (item) {
        openDeleteModal(item);
        return;
      }
    }
    // Simple inline confirm for multi-delete
    const confirmed = window.confirm(
      `Delete ${selectedIds.length} demand example(s)? This cannot be undone.`,
    );
    if (!confirmed) return;
    handleBulkDelete();
  };

  const openFilePreview = (example: DemandExample) => {
    setPreviewExample(example);

    // Use setTimeout to ensure state updates are completed before opening modal
    setTimeout(() => {
      setShowFilePreview(true);
    }, 0);
  };

  const openTemplatesDialog = (example: DemandExample) => {
    navigate(`/demand-examples/${example.id}/templates`);
  };

  const handleCreateSubmit = async () => {
    try {
      const payload = {
        title: createTitle,
        demand: createFile || undefined,
        firm: createFirmId || undefined,
      };

      await createExample(payload).unwrap();
      setShowCreateModal(false);
      resetCreateForm();
      refetch();
    } catch (err) {
      toast.error('Error creating demand example');
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedExample) return;

    try {
      const payload = {
        ...editFormData,
        matter_type: editFormData.matter_type || undefined,
        jurisdiction:
          editFormData.jurisdiction.length > 0
            ? editFormData.jurisdiction
            : undefined,
        practice_areas:
          editFormData.practice_areas.length > 0
            ? editFormData.practice_areas
            : undefined,
        tags: editFormData.tags.length > 0 ? editFormData.tags : undefined,
        firm: editFormData.firm_id || undefined,
      };

      await updateExample({ id: selectedExample.id, data: payload }).unwrap();
      setShowEditModal(false);
      resetEditForm();
      setSelectedExample(null);
      refetch();
    } catch (err) {
      toast.error('Error updating demand example');
    }
  };

  const handleDelete = async () => {
    if (!selectedExample) return;

    try {
      await deleteExample(selectedExample.id).unwrap();
      setShowDeleteModal(false);
      setSelectedExample(null);
      refetch();
    } catch (err) {
      toast.error('Error deleting demand example');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    try {
      const results = await Promise.allSettled(
        selectedIds.map((id) => deleteExample(id).unwrap()),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      setSelectedIds([]);
      refetch();
      if (failed > 0) toast.error(`Failed to delete ${failed} item(s)`);
    } catch (err) {
      toast.error('Error deleting selected demand examples');
    }
  };

  const handleEditFormChange = <K extends keyof FormData>(
    field: K,
    value: FormData[K],
  ) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    resetCreateForm();
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    resetEditForm();
    setSelectedExample(null);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedExample(null);
  };

  const closeFilePreview = () => {
    setShowFilePreview(false);
    setPreviewExample(null);
  };

  // Computed values
  const currentPage =
    Math.floor((filters.offset || 0) / (filters.limit || 25)) + 1;
  const totalPages = demandExamples
    ? Math.ceil(demandExamples.count / (filters.limit || 25))
    : 0;

  return (
    <div className="space-y-6">
      <DemandExamplesHeader
        onAddClick={openCreateModal}
        isMultiSelect={isMultiSelect}
        selectedCount={selectedIds.length}
        onToggleMultiSelect={(next) => {
          setIsMultiSelect(next);
          if (!next) setSelectedIds([]);
        }}
        onClearSelection={() => setSelectedIds([])}
        onDeleteSelected={openBulkDeleteConfirm}
      />

      <DemandExamplesFilters
        filters={filters}
        data={demandExamples}
        isLoading={isLoading}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
      />

      {/* When multi-select is enabled, render a selection-friendly table using the selector component */}
      {isMultiSelect ? (
        <div className="bg-white rounded-lg border border-zinc-200">
          <div className="p-3 border-b flex items-center justify-between">
            <span className="text-sm text-zinc-700">
              Select items to act on, then use actions above.
            </span>
          </div>
          <div className="p-3">
            <div className="max-h-[70vh] overflow-hidden">
              <DemandExampleSelector
                allowMultiSelect
                defaultMultiSelect
                selectedIds={selectedIds}
                onSelectMany={(ids) => setSelectedIds(ids)}
                className="max-h-[70vh] overflow-hidden"
              />
            </div>
          </div>
        </div>
      ) : (
        <DemandExamplesTable
          data={demandExamples}
          isLoading={isLoading}
          error={error}
          currentPage={currentPage}
          totalPages={totalPages}
          onEdit={openEditModal}
          onDelete={openDeleteModal}
          onPageChange={handlePageChange}
          onFilePreview={openFilePreview}
          onManageTemplates={openTemplatesDialog}
        />
      )}

      <CreateDemandExampleModal
        isOpen={showCreateModal}
        title={createTitle}
        selectedFile={createFile}
        firmId={createFirmId}
        isLoading={isCreating}
        onClose={closeCreateModal}
        onSubmit={handleCreateSubmit}
        onTitleChange={setCreateTitle}
        onFileChange={setCreateFile}
        onFirmChange={setCreateFirmId}
      />

      <EditDemandExampleModal
        isOpen={showEditModal}
        selectedExample={selectedExample}
        formData={editFormData}
        isLoading={isUpdating}
        onClose={closeEditModal}
        onSubmit={handleEditSubmit}
        onFormChange={handleEditFormChange}
      />

      <DemandExampleDeleteModal
        isOpen={showDeleteModal}
        selectedExample={selectedExample}
        isLoading={isDeleting}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
      />

      <FilePreviewModal
        isOpen={showFilePreview}
        example={previewExample}
        onClose={closeFilePreview}
      />
    </div>
  );
};
